import * as Yup from "yup";
import { fieldTypes } from "../form/fieldTypes";
import {
  postalCodeRegExp,
  streetNameRegExp,
  cityNameRegExp,
  phoneRegExp,
  bandRegExp,
  familyRegExp,
  positionRegExp,
  fnibRegExp,
  poBoxRegExp,
} from "../../utils/globalUtility";
import {
  isValidBranchNumber,
  isValidBankAccount,
} from "../../utils/bankValidation";

export const STREET_ADDRESS_POBOX_ERROR_RESOURCE_STRING =
  "RESOURCES@form.streetAddressLine1.isPOBoxError";

export const canadaAddressSchema = Yup.object().shape({
  isPOBox: Yup.string().test(
    "isPOBox-required",
    "RESOURCES@global.error.selectAnswer",
    function (value) {
      return value && value?.trim()?.length > 0;
    },
  ),
  streetAddressLine1: Yup.string()
    .test(
      "required-if-no-country",
      "RESOURCES@form.streetAddressLine1.error",
      function (value) {
        const { showCountry } = this.options.context; // Access showCountry from context
        if (showCountry === "true") return true; // Not required if showCountry is 'true'
        return value && value?.trim()?.length > 0; // Required if showCountry !== 'true'
      },
    )
    .test(
      "pattern-when-no-country",
      "RESOURCES@form.streetAddressLine1.invalidError",
      function (value) {
        const { showCountry } = this.options.context; // Access showCountry from context
        if (showCountry === "true") return true; // Skip pattern check if showCountry is 'true'
        return streetNameRegExp.test(value); // Apply pattern if showCountry !== 'true'
      },
    )
    .test(
      "max-length-when-no-country",
      "RESOURCES@form.streetAddressLine1.maxError",
      function (value) {
        return value?.length <= 50; // Apply max length check if showCountry !== 'true'
      },
    )
    .test(
      "po-box-mismatch",
      STREET_ADDRESS_POBOX_ERROR_RESOURCE_STRING,
      function (value) {
        const { isPOBox } = this.parent;
        if (!value) return true;
        if (poBoxRegExp.test(value) && isPOBox === "N") {
          return false;
        }
        return true;
      },
    ),

  streetAddressLine2: Yup.string()
    .matches(streetNameRegExp, "RESOURCES@form.streetAddressLine2.invalidError")
    .max(50, "RESOURCES@form.streetAddressLine2.maxError")
    .nullable()
    .notRequired()
    .transform((value, originalValue) => (originalValue === "" ? null : value)),

  locality: Yup.string()
    .matches(cityNameRegExp, "RESOURCES@form.locality.invalidError")
    .trim()
    .required("RESOURCES@form.locality.error"),

  province: Yup.string().test(
    "required-if-no-country",
    "RESOURCES@form.province.error",
    function (value) {
      const { showCountry } = this.options.context;
      if (showCountry === "true") return true; // Not required if showCountry is 'true'
      return value && value?.trim()?.length > 0; // Required if showCountry !== 'true'
    },
  ),

  postalCode: Yup.string()
    .test(
      "required-if-no-country",
      "RESOURCES@form.postalCode.error",
      function (value) {
        const { showCountry } = this.options.context;
        if (showCountry === "true") return true; // Not required if showCountry is 'true'
        return value && value?.trim()?.length > 0; // Required if showCountry !== 'true'
      },
    )
    .test(
      "pattern-when-no-country",
      "RESOURCES@form.postalCode.invalidError",
      function (value) {
        const { showCountry } = this.options.context; // Access showCountry from context
        if (showCountry === "true") return true; // Skip pattern check if showCountry is 'true'
        return postalCodeRegExp.test(value); // Apply pattern if showCountry !== 'true'
      },
    ),

  country: Yup.string().test(
    "required-if-showCountry-true",
    "RESOURCES@form.country.error",
    function (value) {
      const { showCountry } = this.options.context;
      return showCountry === "true" ? value && value?.trim()?.length > 0 : true; // Required if showCountry === 'true'
    },
  ),
});

// Module-level caches for Yup schema validations
const branchValidationCache = new Map();
const accountValidationCache = new Map();

export const bankDetailsSchema = Yup.object().shape({
  bankName: Yup.string(),
  institutionNumber: Yup.string()
    .required("Enter the Institution number.")
    .matches(/^\d{3}$/, "Enter a valid Institution number.")
    .test(
      "is-valid-institution",
      "Enter a valid Institution number.",
      function (value) {
        return !!this.parent.bankName; // This relies on bankName being set by validateAndSetBankName
      },
    ),
  transitNumber: Yup.string()
    .required("Enter the transit/branch number.")
    .matches(/^\d{5}$/, "Enter a valid transit/branch number.")
    .test(
      "API Validation",
      "Enter a valid transit/branch number.",
      async function (value) {
        if (
          !value ||
          value?.length < 5 ||
          !this.parent.institutionNumber ||
          !this.parent.bankName
        ) {
          return false;
        }
        const cacheKey = `${this.parent.institutionNumber}-${value}`;
        if (branchValidationCache.has(cacheKey)) {
          return branchValidationCache.get(cacheKey);
        }
        const apiResponse = await isValidBranchNumber(
          this.parent.institutionNumber,
          value,
        );
        const isValid = !!apiResponse?.result?.data;
        branchValidationCache.set(cacheKey, isValid);
        return isValid;
      },
    ),
  bankAccount: Yup.string()
    .required("Enter the Bank account number")
    .test(
      "API Validation",
      "Enter a valid bank account number.",
      async function (value) {
        if (
          !value ||
          value?.length < 7 ||
          value?.length > 12 ||
          !this.parent.institutionNumber ||
          !this.parent.bankName ||
          !this.parent.transitNumber
        ) {
          return false;
        }
        const cacheKey = `${this.parent.institutionNumber}-${this.parent.transitNumber}-${value}`;
        if (accountValidationCache.has(cacheKey)) {
          return accountValidationCache.get(cacheKey);
        }
        const apiResponse = await isValidBankAccount(
          this.parent.institutionNumber,
          this.parent.transitNumber,
          value,
        );
        const isValid = !!apiResponse?.result?.data;
        accountValidationCache.set(cacheKey, isValid);
        return isValid;
      },
    ),
  confirmBankAccount: Yup.string()
    .required("Enter the Bank account number")
    .oneOf([Yup.ref("bankAccount")], "Bank account numbers must match"),
});

export const phoneMobileSchema = Yup.object().shape({
  mobile: Yup.string()
    .matches(phoneRegExp, "RESOURCES@form.phone.invalidError")
    .min(10, "RESOURCES@form.phone.invalidError")
    .max(20, "RESOURCES@form.phone.invalidError")
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .test("one-of-required", "", function (value) {
      // Check if both fields are null or empty strings
      let valid =
        !(this.parent.mobile === null || this.parent.mobile === "") ||
        !(this.parent.phone === null || this.parent.phone === "");
      return valid;
    }),
  phone: Yup.string()
    .matches(phoneRegExp, "RESOURCES@form.phone.invalidError")
    .min(10, "RESOURCES@form.phone.invalidError")
    .max(20, "RESOURCES@form.phone.invalidError")
    .nullable()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .test("one-of-required", "", function (value) {
      // Check if both fields are null or empty strings
      let valid =
        !(this.parent.mobile === null || this.parent.mobile === "") ||
        !(this.parent.phone === null || this.parent.phone === "");
      return valid;
    }),
});

export const familyMembersSchema = Yup.object().shape({
  acceptLegalRepresentative: Yup.bool().when("familyMembers", {
    is: (familyMembers) =>
      Array.isArray(familyMembers) && familyMembers?.length > 0,
    then: () =>
      Yup.bool()
        .oneOf(
          [true],
          "RESOURCES@familyMembers.acceptLegalRepresentative.acceptError",
        )
        .required(
          "RESOURCES@familyMembers.acceptLegalRepresentative.acceptError",
        ),
    otherwise: () => Yup.bool().notRequired(),
  }),
});

export const firstNationStatusDetailSchema = Yup.object().shape({
  band: Yup.string()
    .nullable()
    .notRequired()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .when(["isIndigenous"], {
      is: (isIndigenous) => isIndigenous === "Y",
      then: (schema) =>
        schema.matches(bandRegExp, "RESOURCES@global.error.invalidLength"),
      otherwise: (schema) => schema,
    }),
  family: Yup.string()
    .nullable()
    .notRequired()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .when(["isIndigenous"], {
      is: (isIndigenous) => isIndigenous === "Y",
      then: (schema) =>
        schema.matches(familyRegExp, "RESOURCES@global.error.invalidLength"),
      otherwise: (schema) => schema,
    }),
  position: Yup.string()
    .nullable()
    .notRequired()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .when(["isIndigenous"], {
      is: (isIndigenous) => isIndigenous === "Y",
      then: (schema) =>
        schema.matches(positionRegExp, "RESOURCES@global.error.invalidLength"),
      otherwise: (schema) => schema,
    }),
  FNIHB: Yup.string()
    .nullable()
    .notRequired()
    .transform((value, originalValue) => (originalValue === "" ? null : value))
    .when(["isIndigenous"], {
      is: (isIndigenous) => isIndigenous === "Y",
      then: (schema) =>
        schema.matches(fnibRegExp, "RESOURCES@fnihb.error.invalidLength"),
      otherwise: (schema) => schema,
    }),
});

export const mapLocationSchema = Yup.object().shape({
  addressNotFound: Yup.bool(),
  mapLat: Yup.number().when("addressNotFound", {
    is: (addressNotFound) => !addressNotFound,
    then: () => Yup.number().required("RESOURCES@form.marker.error"),
  }),
  mapLng: Yup.number().when("addressNotFound", {
    is: (addressNotFound) => !addressNotFound,
    then: () => Yup.number().required("RESOURCES@form.marker.error"),
  }),
  location: Yup.string().when("addressNotFound", {
    is: (addressNotFound) => !!addressNotFound,
    then: () =>
      Yup.string()
        .required("RESOURCES@form.location.error")
        .max(400, "RESOURCES@form.locationDescription.maxLengthError"),
  }),
  acceptDeclaration: Yup.boolean().when(
    ["addressNotFound", "mapLat", "mapLng", "location"],
    {
      is: (addressNotFound, mapLat, mapLng, location) => {
        // Has the user finished the address part?
        return addressNotFound ? !!location : !!mapLat && !!mapLng; // If location is filled, check location else cordinates
      },
      then: () =>
        Yup.boolean().oneOf([true], "RESOURCES@form.acceptDeclaration.error"), // If address is filled -> Declaration is strictly required
      otherwise: () => Yup.boolean(), // Else (address missing) -> Hiding the Declaration validation
    },
  ),
});

export const dateOfBirthSchema = Yup.object().shape({
  dob: Yup.date()
    .nullable()
    .transform((value, originalValue) => {
      // Transform empty string to null to avoid "Invalid Date" error
      if (
        originalValue === "" ||
        originalValue === null ||
        originalValue === undefined
      ) {
        return null;
      }
      return value;
    })
    .max(
      new Date(new Date().setDate(new Date().getDate() - 1)),
      "RESOURCES@form.dob.invalidError",
    )
    .required("RESOURCES@form.dob.error"),
});

export const WIDGET_SCHEMAS = {
  [fieldTypes.dateWidget]: dateOfBirthSchema,
  [fieldTypes.phoneWidget]: phoneMobileSchema,
  [fieldTypes.addressWidget]: canadaAddressSchema,
  [fieldTypes.firstNationStatusWidget]: firstNationStatusDetailSchema,
  [fieldTypes.familyMembersWidget]: familyMembersSchema,
  [fieldTypes.mapLocationWidget]: mapLocationSchema,
  [fieldTypes.bankDetailsWidget]: bankDetailsSchema,
};
